import React from 'react';

export interface LoadingSpinnerProps {
  /**
   * Loading message to display
   * @default 'Loading...'
   */
  message?: string;

  /**
   * Size of the spinner
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Show message below spinner
   * @default true
   */
  showMessage?: boolean;

  /**
   * Additional CSS classes for container
   */
  className?: string;
}

/**
 * LoadingSpinner Component
 *
 * Displays a consistent loading spinner with optional message.
 * Used for loading states across the application.
 *
 * @example
 * ```tsx
 * <LoadingSpinner />
 * <LoadingSpinner message="Loading projects..." />
 * <LoadingSpinner size="lg" showMessage={false} />
 * ```
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = 'Loading...',
  size = 'md',
  showMessage = true,
  className = '',
}) => {
  const sizeMap = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  const spinnerSize = sizeMap[size];

  return (
    <div className={`flex items-center justify-center h-64 ${className}`}>
      <div className="flex flex-col items-center gap-3">
        {/* Spinner */}
        <div
          className={`${spinnerSize} animate-spin rounded-full border-4 border-gray-300 border-t-blue-600`}
          role="status"
          aria-label="Loading"
        />

        {/* Message */}
        {showMessage && (
          <p className="text-sm text-gray-600">{message}</p>
        )}
      </div>
    </div>
  );
};

export default LoadingSpinner;
